/**
 * The report catalogue, on screen.
 *
 * SHAPED AGAINST BACKUP RADAR on purpose: three groups, their names, their
 * filter vocabulary. An MSP switching across will look for these by name.
 *
 * THE UNBUILT ONES ARE SHOWN, GREYED, WITH THE REASON. Six of the fourteen
 * need records we do not keep yet. Hiding them would flatter us to whoever is
 * comparing the two products - and would quietly mislead us later, when
 * nobody remembers why the list was short.
 *
 * CAVEATS RENDER ABOVE THE TABLE, not below it. A report that has narrowed its
 * own scope has to say so where it will be read; underneath a hundred rows is
 * the same as not saying it.
 */

import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { errText } from './btApi';
import { Modal } from './components/Modal';
import { ReportSchedules } from './components/ReportSchedules';
import { useCan } from './org';

function call<TIn extends object, TOut>(name: string) {
  return async (data: TIn): Promise<TOut> => (await httpsCallable<TIn, TOut>(functions, name)(data)).data;
}

type Group = 'backup' | 'compliance' | 'administrative';
type FilterKey = 'dateRange' | 'clients' | 'statuses' | 'sources' | 'includeRetired' | 'includeWeekends';

interface ReportDef {
  id: string;
  group: Group;
  label: string;
  blurb: string;
  filters: FilterKey[];
  unavailable?: string;
}

interface Column { key: string; label: string; numeric?: boolean }

interface RunResult {
  reportId: string;
  label: string;
  generatedAtMs: number;
  table: {
    columns: Column[];
    rows: Array<Record<string, string | number | null>>;
    summary: Array<{ label: string; value: string }>;
    caveats: string[];
  };
}

const listReportTypes = call<{ orgId: string }, { reports: ReportDef[] }>('listReportTypes');
const runReport = call<{ orgId: string; reportId: string; filters: unknown }, RunResult>('runReport');
const runReportPdf = call<{ orgId: string; reportId: string; filters: unknown },
  { fileName: string; base64: string; bytes: number }>('runReportPdf');

const GROUPS: Array<{ key: Group; label: string }> = [
  { key: 'backup', label: 'Backup' },
  { key: 'compliance', label: 'Compliance' },
  { key: 'administrative', label: 'Administrative' },
];

const STATUSES = ['healthy', 'warning', 'failed', 'paused', 'unknown'] as const;
const SOURCES = [
  { id: 'datto', label: 'Datto' },
  { id: 'sp', label: 'ShadowProtect' },
  { id: 'sc', label: 'Image Manager' },
  { id: 'msp', label: 'MSP360' },
  { id: 'unitrends', label: 'Unitrends' },
];

const DAY = 86_400_000;
const iso = (ms: number) => new Date(ms).toISOString().slice(0, 10);

export function ReportsPanel({ orgId }: { orgId: string }) {
  const [defs, setDefs] = useState<ReportDef[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<ReportDef | null>(null);
  const [result, setResult] = useState<RunResult | null>(null);
  const [busy, setBusy] = useState(false);
  // Two tabs, the same split Backup Radar uses - somebody switching across
  // looks for Scheduled Reports as a sibling of On Demand, not buried.
  const [tab, setTab] = useState<'ondemand' | 'scheduled'>('ondemand');
  const can = useCan();
  // Kept so the PDF is built from the SAME filters as the table on screen,
  // rather than from whatever the dialog happens to hold now.
  const [lastFilters, setLastFilters] = useState<unknown>({});

  useEffect(() => {
    void (async () => {
      setLoading(true); setError(null);
      try { setDefs((await listReportTypes({ orgId })).reports); }
      catch (e) { setError(errText(e)); }
      finally { setLoading(false); }
    })();
  }, [orgId]);

  return (
    <section className="panel">
      <h2 className="panel-title">Reports</h2>
      <div className="rep-tabs">
        <button type="button" className={'rep-tab' + (tab === 'ondemand' ? ' on' : '')}
                onClick={() => setTab('ondemand')}>On demand</button>
        <button type="button" className={'rep-tab' + (tab === 'scheduled' ? ' on' : '')}
                onClick={() => setTab('scheduled')}>Scheduled reports</button>
      </div>

      {tab === 'scheduled' && (
        <ReportSchedules orgId={orgId} reports={defs} canManage={can('reports.send')} />
      )}

      {error && <div className="banner banner-error">{error}</div>}
      {tab === 'ondemand' && (loading && defs.length === 0 ? <div className="empty">Loading…</div> : (
        <div className="rep-groups">
          {GROUPS.map((g) => (
            <div key={g.key} className="rep-group">
              <h3 className="rep-group-title">{g.label}</h3>
              <ul className="rep-list">
                {defs.filter((d) => d.group === g.key).map((d) => (
                  <li key={d.id}>
                    <button
                      type="button"
                      className={'rep-item' + (d.unavailable ? ' off' : '')}
                      disabled={Boolean(d.unavailable)}
                      title={d.unavailable ?? d.blurb}
                      onClick={() => { setPicked(d); setResult(null); }}
                    >
                      <span className="rep-name">{d.label}</span>
                      <span className="rep-blurb">{d.unavailable ?? d.blurb}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ))}

      {picked && !result && (
        <FilterDialog
          def={picked}
          busy={busy}
          onClose={() => setPicked(null)}
          onRun={async (filters) => {
            setBusy(true); setError(null);
            setLastFilters(filters);
            try { setResult(await runReport({ orgId, reportId: picked.id, filters })); }
            catch (e) { setError(errText(e)); setPicked(null); }
            finally { setBusy(false); }
          }}
        />
      )}

      {result && (
        <ResultView
          orgId={orgId}
          filters={lastFilters}
          result={result}
          onClose={() => { setResult(null); setPicked(null); }}
          onBack={() => setResult(null)}
        />
      )}
    </section>
  );
}

function FilterDialog({ def, busy, onClose, onRun }: {
  def: ReportDef;
  busy: boolean;
  onClose: () => void;
  onRun: (filters: unknown) => void;
}) {
  const now = Date.now();
  const [from, setFrom] = useState(iso(now - 7 * DAY));
  const [to, setTo] = useState(iso(now));
  const [statuses, setStatuses] = useState<string[]>([]);
  const [sources, setSources] = useState<string[]>([]);
  const [includeRetired, setIncludeRetired] = useState(false);
  const [includeWeekends, setIncludeWeekends] = useState(true);

  const has = (k: FilterKey) => def.filters.includes(k);
  const toggle = (list: string[], set: (v: string[]) => void, v: string) =>
    set(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  return (
    <Modal title={def.label} subtitle={def.blurb} onClose={onClose}>
      {has('dateRange') && (
        <div className="row-form">
          <label className="field">
            <span className="field-label">From</span>
            <input className="field-input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="field">
            <span className="field-label">To</span>
            <input className="field-input" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
          <div className="field">
            <span className="field-label">&nbsp;</span>
            <div className="row" style={{ gap: 6 }}>
              {[7, 30, 90].map((n) => (
                <button key={n} type="button" className="btn"
                        onClick={() => { setFrom(iso(now - n * DAY)); setTo(iso(now)); }}>
                  Last {n} days
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {has('statuses') && (
        <>
          <div className="field-label" style={{ marginTop: 10 }}>Statuses</div>
          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {STATUSES.map((s) => (
              <button key={s} type="button"
                      className={'btn' + (statuses.includes(s) ? ' btn-primary' : '')}
                      onClick={() => toggle(statuses, setStatuses, s)}>{s}</button>
            ))}
          </div>
          <div className="field-hint">Nothing selected means every status.</div>
        </>
      )}

      {has('sources') && (
        <>
          <div className="field-label" style={{ marginTop: 10 }}>Sources</div>
          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {SOURCES.map((s) => (
              <button key={s.id} type="button"
                      className={'btn' + (sources.includes(s.id) ? ' btn-primary' : '')}
                      onClick={() => toggle(sources, setSources, s.id)}>{s.label}</button>
            ))}
          </div>
          <div className="field-hint">Nothing selected means every source.</div>
        </>
      )}

      {has('includeRetired') && (
        <label className="check-row" style={{ marginTop: 10 }}>
          <input type="checkbox" checked={includeRetired} onChange={(e) => setIncludeRetired(e.target.checked)} />
          <span>Include retired machines</span>
        </label>
      )}
      {has('includeWeekends') && (
        <label className="check-row">
          <input type="checkbox" checked={includeWeekends} onChange={(e) => setIncludeWeekends(e.target.checked)} />
          <span>Include weekends</span>
        </label>
      )}

      <div className="row-form" style={{ marginTop: 14 }}>
        <button
          className="btn btn-primary" disabled={busy}
          onClick={() => onRun({
            // Parsed as UTC noon so a date picked in the browser cannot slide a
            // day either way on the server.
            fromMs: Date.parse(`${from}T12:00:00Z`),
            toMs: Date.parse(`${to}T12:00:00Z`),
            statuses, sources, includeRetired, includeWeekends,
            clients: [],
          })}
        >
          {busy ? 'Generating…' : 'Generate'}
        </button>
        <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
      </div>
    </Modal>
  );
}

function ResultView({ result, orgId, filters, onClose, onBack }: {
  result: RunResult; orgId: string; filters: unknown; onClose: () => void; onBack: () => void;
}) {
  const { table } = result;
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfErr, setPdfErr] = useState<string | null>(null);

  async function downloadPdf() {
    setPdfBusy(true); setPdfErr(null);
    try {
      const r = await runReportPdf({ orgId, reportId: result.reportId, filters });
      // Base64 rather than a signed URL: nothing to secure and nothing left
      // lying in a bucket afterwards.
      const bin = atob(r.base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      a.download = r.fileName;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { setPdfErr(errText(e)); }
    finally { setPdfBusy(false); }
  }


  return (
    <div style={{ marginTop: 16 }}>
      <div className="integ-head">
        <div>
          <h3 className="panel-title" style={{ margin: 0 }}>{result.label}</h3>
          <div className="dim" style={{ fontSize: 12 }}>
            Generated {new Date(result.generatedAtMs).toLocaleString()}
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn" disabled={pdfBusy} onClick={() => void downloadPdf()}>
            {pdfBusy ? 'Buildingâ€¦' : 'Download PDF'}
          </button>
          <button className="btn" onClick={() => downloadCsv(result)}>Download CSV</button>
          <button className="btn" onClick={onBack}>Change filters</button>
          <button className="btn-link" onClick={onClose}>close</button>
        </div>
      </div>

      {pdfErr && <div className="banner banner-error">{pdfErr}</div>}
      {table.summary.length > 0 && (
        <div className="rep-summary">
          {table.summary.map((s) => (
            <div key={s.label} className="rep-stat">
              <div className="rep-stat-value">{s.value}</div>
              <div className="rep-stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Above the table, deliberately. A caveat under a hundred rows is a
          caveat nobody reads. */}
      {table.caveats.map((c) => (
        <div key={c} className="banner banner-warn">{c}</div>
      ))}

      {table.rows.length === 0 ? (
        <div className="empty">
          Nothing matched. That is an answer, not a failure &mdash; widen the filters if it looks wrong.
        </div>
      ) : (
        <div className="rep-scroll">
          <table className="tbl">
            <thead>
              <tr>{table.columns.map((c) => (
                <th key={c.key} style={c.numeric ? { textAlign: 'right' } : undefined}>{c.label}</th>
              ))}</tr>
            </thead>
            <tbody>
              {table.rows.map((r, i) => (
                <tr key={i}>
                  {table.columns.map((c) => (
                    <td key={c.key} style={c.numeric ? { textAlign: 'right' } : undefined}>
                      {format(r[c.key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Milliseconds render as a date; everything else as itself. */
function format(v: string | number | null | undefined): string {
  if (v === null || v === undefined || v === '') return '';
  // A timestamp is the only number big enough to be a date. Anything smaller is
  // a count and must be printed as one.
  if (typeof v === 'number' && v > 1_000_000_000_000) return new Date(v).toLocaleString();
  return String(v);
}

function downloadCsv(result: RunResult) {
  const { columns, rows } = result.table;
  // Quote everything and double any embedded quote. A machine called
  // SQL01, "the old one" would otherwise split into two columns silently.
  const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [
    columns.map((c) => esc(c.label)).join(','),
    ...rows.map((r) => columns.map((c) => esc(format(r[c.key]))).join(',')),
  ].join('\r\n');

  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${result.reportId}-${new Date(result.generatedAtMs).toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}
